import { useEffect, useState } from "react";
import { api, idr } from "../lib/api";
import { toast } from "sonner";
import AttachmentPanel from "./finance/AttachmentPanel";
import {
  HandCoins, Plus, X, Paperclip, Trash2, Clock, CheckCircle2,
  Wallet, XCircle, FileWarning, Info,
} from "lucide-react";
import InputRupiah from "../components/InputRupiah";
import { DatePicker } from "@/components/ui/date-picker";
import LoadingState from "@/components/ui/loading-state";
import { hariIniISO } from "@/lib/tanggal";

const STATUS_META = {
  pending: { label: "Menunggu persetujuan", chip: "bg-amber-100 text-amber-800", icon: Clock },
  approved: { label: "Disetujui, menunggu transfer", chip: "bg-sky-100 text-sky-800", icon: CheckCircle2 },
  paid: { label: "Sudah dibayar", chip: "bg-emerald-100 text-emerald-800", icon: Wallet },
  rejected: { label: "Ditolak", chip: "bg-rose-100 text-rose-800", icon: XCircle },
  void: { label: "Dibatalkan", chip: "bg-slate-100 text-slate-600", icon: XCircle },
};

const emptyForm = {
  tanggal: hariIniISO(),
  kategori_beban_id: "",
  nominal: "",
  no_invoice: "",
  tanpa_invoice: false,
  alasan_tanpa_invoice: "",
  keterangan: "",
};

export default function MyReimbursement() {
  const [data, setData] = useState(null);
  const [beban, setBeban] = useState([]);
  const [form, setForm] = useState(null);
  const [attFor, setAttFor] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/finance/reimbursement/mine");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat klaim");
      setData({ items: [], summary: {}, linked: true });
    }
  };

  useEffect(() => {
    load();
    api.get("/finance/reimbursement/options")
      .then(r => setBeban(r.data.beban || []))
      .catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post("/finance/reimbursement/mine", {
        ...form, nominal: Number(form.nominal) || 0,
      });
      toast.success(r.data.message || "Klaim dikirim");
      setForm(null);
      await load();
      // Langsung buka panel lampiran — nota tanpa foto sering ditolak Finance
      if (r.data.item) setAttFor(r.data.item);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim klaim");
    } finally { setBusy(false); }
  };

  const hapus = async (it) => {
    if (!window.confirm(`Hapus klaim ${it.code}?`)) return;
    try {
      await api.delete(`/finance/reimbursement/${it.id}`);
      toast.success("Klaim dihapus");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };

  if (!data) return <div className="p-4 md:p-8"><LoadingState loading data-testid="my-reimbursement-loading" /></div>;

  if (data.linked === false) {
    return (
      <div className="p-4 md:p-8">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-800 flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Akun Anda belum terhubung ke data karyawan, jadi klaim reimbursement belum bisa diajukan. Hubungi HR atau Admin untuk menghubungkannya.</span>
        </div>
      </div>
    );
  }

  const s = data.summary || {};
  const cards = [
    { label: "Menunggu Persetujuan", v: s.pending, tint: "text-amber-700" },
    { label: "Disetujui, Belum Cair", v: s.approved, tint: "text-sky-700" },
    { label: "Sudah Dibayar", v: s.paid, tint: "text-emerald-700" },
    { label: "Ditolak", v: s.rejected, tint: "text-rose-700" },
  ];

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="my-reimbursement">
      <div className="flex items-start justify-between flex-col md:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Portal Karyawan</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Reimbursement Saya</h1>
          <p className="text-sm text-slate-600 mt-1">Klaim uang yang Anda talangi untuk keperluan kantor.</p>
        </div>
        <button onClick={() => setForm({ ...emptyForm })} data-testid="myrmb-add"
                className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> Ajukan Klaim
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map(c => (
          <div key={c.label} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{c.label}</p>
            <p className={`font-display text-xl font-bold mt-2 ${c.tint}`}>{idr(c.v?.total || 0)}</p>
            <p className="text-[11px] text-slate-500 mt-0.5">{c.v?.count || 0} klaim</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="px-5 py-4 border-b border-slate-100">
          <p className="font-display font-bold text-base text-slate-900">Riwayat Klaim</p>
          <p className="text-xs text-slate-500 mt-0.5">Lampirkan foto nota selagi status masih "menunggu" — setelah diproses Finance, lampiran terkunci.</p>
        </div>
        <div className="p-3 space-y-2">
          {data.items.length === 0 ? (
            <p className="text-center text-sm text-slate-400 py-10">Belum ada klaim. Tekan "Ajukan Klaim" untuk mulai.</p>
          ) : data.items.map(it => {
            const meta = STATUS_META[it.status] || STATUS_META.pending;
            const Icon = meta.icon;
            return (
              <div key={it.id} data-testid={`myrmb-row-${it.id}`}
                   className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50/60">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${meta.chip}`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </span>
                      <span className="font-mono text-[11px] text-slate-400">{it.code}</span>
                    </div>
                    <p className="text-sm font-medium text-slate-900 mt-1.5">{it.kategori_beban_name}</p>
                    <p className="text-xs text-slate-500">
                      {it.tanggal}
                      {" · "}
                      {it.tanpa_invoice
                        ? <span className="text-amber-700 inline-flex items-center gap-1"><FileWarning className="w-3 h-3" />tanpa invoice</span>
                        : <>No. {it.no_invoice}</>}
                      {(it.attachments?.length || 0) > 0 && ` · ${it.attachments.length} lampiran`}
                    </p>
                    {it.keterangan && <p className="text-xs text-slate-600 mt-1">{it.keterangan}</p>}
                    {it.status === "rejected" && it.rejection_reason && (
                      <p className="text-xs text-rose-700 mt-1.5 bg-rose-50 border border-rose-200 rounded px-2 py-1">
                        Alasan ditolak: {it.rejection_reason}
                      </p>
                    )}
                    {it.status === "paid" && (
                      <p className="text-xs text-emerald-700 mt-1.5">Ditransfer {it.tanggal_bayar} lewat {it.sumber_akun_name}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <p className="font-mono font-semibold text-slate-900">{idr(it.nominal)}</p>
                    <button onClick={() => setAttFor(it)} data-testid={`myrmb-att-${it.id}`} title="Foto nota"
                            className="w-8 h-8 rounded grid place-items-center hover:bg-slate-100 text-slate-600">
                      <Paperclip className="w-4 h-4" />
                    </button>
                    {it.status === "pending" && (
                      <button onClick={() => hapus(it)} data-testid={`myrmb-del-${it.id}`} title="Hapus"
                              className="w-8 h-8 rounded grid place-items-center hover:bg-rose-50 text-rose-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setForm(null)}>
          <div onClick={e => e.stopPropagation()} data-testid="myrmb-modal"
               className="bg-white rounded-2xl max-w-md w-full max-h-[88vh] flex flex-col shadow-2xl">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-display font-bold text-lg flex items-center gap-2">
                <HandCoins className="w-5 h-5 text-emerald-600" /> Ajukan Klaim
              </h3>
              <button onClick={() => setForm(null)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 overflow-y-auto flex-1 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <F label="Tanggal pengeluaran">
                  <DatePicker value={form.tanggal} data-testid="myrmb-f-tanggal"
                         onChange={v => setForm(f => ({ ...f, tanggal: v }))} />
                </F>
                <F label="Nominal">
                  <InputRupiah value={form.nominal} testid="myrmb-f-nominal"
                         onChange={n => setForm(f => ({ ...f, nominal: n }))} className={inp} />
                </F>
              </div>
              <F label="Untuk apa">
                <select value={form.kategori_beban_id} data-testid="myrmb-f-kategori"
                        onChange={e => setForm(f => ({ ...f, kategori_beban_id: e.target.value }))} className={inp}>
                  <option value="">— Pilih kategori —</option>
                  {beban.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </F>
              <F label="No. invoice / nota">
                <input value={form.no_invoice} disabled={form.tanpa_invoice} placeholder="Nomor yang tertera di struk"
                       data-testid="myrmb-f-invoice"
                       onChange={e => setForm(f => ({ ...f, no_invoice: e.target.value }))}
                       className={`${inp} disabled:bg-slate-100 disabled:text-slate-400`} />
              </F>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.tanpa_invoice} data-testid="myrmb-f-tanpa-invoice"
                       onChange={e => setForm(f => ({ ...f, tanpa_invoice: e.target.checked }))}
                       className="w-4 h-4 rounded border-slate-300" />
                Pengeluaran ini tidak ada notanya
              </label>
              {form.tanpa_invoice && (
                <F label="Kenapa tidak ada nota?" hint="Wajib diisi supaya Finance bisa menilai">
                  <input value={form.alasan_tanpa_invoice} placeholder="Contoh: parkir tanpa struk"
                         data-testid="myrmb-f-alasan"
                         onChange={e => setForm(f => ({ ...f, alasan_tanpa_invoice: e.target.value }))} className={inp} />
                </F>
              )}
              <F label="Keterangan">
                <textarea rows={2} value={form.keterangan} placeholder="Jelaskan singkat keperluannya"
                          data-testid="myrmb-f-ket"
                          onChange={e => setForm(f => ({ ...f, keterangan: e.target.value }))} className={inp} />
              </F>
              <p className="text-[11px] text-slate-500">Setelah disimpan, panel foto nota langsung terbuka supaya Anda bisa unggah dari HP.</p>
            </div>
            <div className="p-4 border-t border-slate-100 flex items-center justify-end gap-2">
              <button onClick={() => setForm(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50">Batal</button>
              <button onClick={save} disabled={busy} data-testid="myrmb-submit"
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                {busy ? "Mengirim..." : "Kirim Klaim"}
              </button>
            </div>
          </div>
        </div>
      )}

      {attFor && (
        <AttachmentPanel module="reimbursement" objId={attFor.id}
                         objLabel={`${attFor.code} · ${idr(attFor.nominal)}`}
                         onClose={() => { setAttFor(null); load(); }} />
      )}
    </div>
  );
}

const inp = "w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200";

function F({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}
